import { useState, useEffect } from 'react'
import { Clock, FileText, Umbrella, HelpCircle, FolderOpen, Star, Calendar, MessageSquare } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useBrandColors } from '../../hooks/useBrandColors'

export default function EmpDashboard() {
  const { accent, accentLight, accentBg } = useBrandColors()
  const navigate = useNavigate()

  const [clockedIn, setClockedIn] = useState(false)
  const [clockInTime, setClockInTime] = useState(null)
  const [elapsed, setElapsed] = useState(0)
  const [showClockModal, setShowClockModal] = useState(false)
  const [jobCode, setJobCode] = useState('')
  const [clockNote, setClockNote] = useState('')
  const [department, setDepartment] = useState('Operations')
  const [punches, setPunches] = useState([
    { in: '8:02 AM', out: '12:00 PM', dept: 'Operations', code: 'OPS-001', note: 'Morning shift', hours: 3.97 },
    { in: '12:45 PM', out: '4:30 PM', dept: 'Operations', code: 'OPS-001', note: '', hours: 3.75 },
  ])

  useEffect(() => {
    if (!clockedIn) return
    const timer = setInterval(() => setElapsed(e => e + 1), 1000)
    return () => clearInterval(timer)
  }, [clockedIn])

  function formatElapsed(secs) {
    const h = Math.floor(secs / 3600)
    const m = Math.floor((secs % 3600) / 60)
    const s = secs % 60
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
  }

  function handleClockIn() {
    const now = new Date()
    const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    setClockedIn(true)
    setClockInTime(timeStr)
    setElapsed(0)
    setPunches(p => [{in: timeStr, out: null, dept: department, code: jobCode || 'OPS-001', note: clockNote, hours: null}, ...p])
    setShowClockModal(false)
    setJobCode('')
    setClockNote('')
  }

  function handleClockOut() {
    const now = new Date()
    const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    const hrs = Math.round((elapsed / 3600) * 100) / 100
    setPunches(p => p.map((punch, i) => i === 0 ? {...punch, out: timeStr, hours: hrs} : punch))
    setClockedIn(false)
    setClockInTime(null)
    setElapsed(0)
  }

  const [urgentMemo, setUrgentMemo] = useState({
    active: true,
    title: 'OFFICE CLOSURE — Weather Emergency',
    message: 'Due to the incoming storm, the office will be closed tomorrow Friday April 5. All staff should work remotely. Check your email for further updates.',
    severity: 'urgent', // urgent | warning | info
    from: 'Tiffany Gallagher',
    time: '2:15 PM today',
  })

  const todos = [
    { text: 'Sign updated Handbook (2025)', badge: 'Due Today', badgeColor: accent, done: false },
    { text: 'Submit timesheet - Week 13', badge: 'Overdue', badgeColor: '#A0392B', done: false },
    { text: 'Complete Safety Training Module 2', badge: 'Apr 10', badgeColor: '#4A6B8A', done: false },
    { text: 'Review & sign PTO Policy update', badge: 'Apr 15', badgeColor: '#4A6B8A', done: false },
    { text: 'Complete I-9 verification', badge: 'Done', badgeColor: '#4A7C59', done: true },
  ]

  const memos = [
    { title: 'Spring Schedule Change - All Staff', date: 'Today', preview: 'Effective April 14, office hours shift to 8am-4pm...', unread: true },
    { title: 'Employee Appreciation Week - Save the Date', date: 'Mar 30', preview: "Mark your calendars! April 22-26 we're celebrating...", unread: true },
    { title: 'Updated Parking Policy', date: 'Mar 25', preview: 'Starting May 1, all staff vehicles must be registered...', unread: false },
  ]

  const calDays = Array.from({length: 35}, (_, i) => {
    const day = i - 1
    if (day < 1 || day > 30) return { label: '', type: 'empty' }
    if (day === 2) return { label: '2', type: 'today' }
    if ([23,24,25,26].includes(day)) return { label: String(day), type: 'pto' }
    if ([10,14,22].includes(day)) return { label: String(day), type: 'event' }
    return { label: String(day), type: 'normal' }
  })

  const calColor = t => t === 'today' ? {background:accent,color:'#fff',fontWeight:700} : t === 'pto' ? {background:'rgba(217,195,163,0.35)',color:'#8a6640'} : t === 'event' ? {background:'rgba(107,125,107,0.15)',color:'#4A7C59'} : t === 'empty' ? {color:'rgba(107,125,107,0.25)'} : {color:'#4A4A4A'}

  const inputStyle = { width:'100%', padding:'8px 12px', border:'1px solid #EDE6DA', borderRadius:6, fontSize:12, fontFamily:"'Montserrat',sans-serif", outline:'none', boxSizing:'border-box' }
  const labelStyle = { display:'block', fontSize:9, textTransform:'uppercase', letterSpacing:'0.1em', color:'#6B7D6B', fontWeight:600, marginBottom:5 }

  const todayTotal = punches.reduce((acc, p) => acc + (p.hours || 0), 0)

  return (
    <div style={{fontFamily:"'Montserrat',sans-serif"}}>

      {/* Urgent Memo Banner */}
      {urgentMemo?.active && (
        <div style={{
          background: urgentMemo.severity==='urgent' ? '#A0392B' : urgentMemo.severity==='warning' ? '#C4843A' : '#4A6B8A',
          padding:'12px 20px', marginBottom:20, borderRadius:8,
          display:'flex', alignItems:'flex-start', gap:12,
          boxShadow:'0 2px 12px rgba(0,0,0,0.15)'
        }}>
          <div style={{fontSize:20,flexShrink:0,marginTop:1}}>
            {urgentMemo.severity==='urgent'?'🚨':urgentMemo.severity==='warning'?'⚠️':'📢'}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3}}>
              <span style={{fontSize:11,fontWeight:700,color:'#fff',textTransform:'uppercase',letterSpacing:'0.1em'}}>{urgentMemo.severity==='urgent'?'Urgent':'Notice'}</span>
              <span style={{fontSize:10,color:'rgba(255,255,255,0.6)'}}>from {urgentMemo.from} · {urgentMemo.time}</span>
            </div>
            <div style={{fontSize:13,fontWeight:600,color:'#fff',marginBottom:2}}>{urgentMemo.title}</div>
            <div style={{fontSize:12,color:'rgba(255,255,255,0.85)',lineHeight:1.5}}>{urgentMemo.message}</div>
          </div>
          <button onClick={()=>setUrgentMemo(null)} style={{background:'rgba(255,255,255,0.15)',border:'none',borderRadius:4,color:'#fff',fontSize:11,fontWeight:600,cursor:'pointer',padding:'4px 10px',flexShrink:0,fontFamily:"'Montserrat',sans-serif"}}>
            Dismiss
          </button>
        </div>
      )}

      {/* Clock In Modal */}
      {showClockModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:24}} onClick={()=>setShowClockModal(false)}>
          <div style={{background:'#fff',borderRadius:12,width:'100%',maxWidth:420,boxShadow:'0 8px 40px rgba(0,0,0,0.18)'}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:'18px 24px',borderBottom:'1px solid #EDE6DA',background:'#2B2B2B',borderRadius:'12px 12px 0 0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:16,fontWeight:600,color:'#fff'}}>Clock In</div>
              <button onClick={()=>setShowClockModal(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'rgba(255,255,255,0.5)',lineHeight:1}}>&times;</button>
            </div>
            <div style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:14}}>
              <div style={{textAlign:'center',padding:'12px',background:accentBg,borderRadius:8}}>
                <div style={{fontSize:11,color:'#6B7D6B',marginBottom:2}}>Current Time</div>
                <div style={{fontFamily:'Georgia,serif',fontSize:24,color:accent,fontWeight:600}}>
                  {new Date().toLocaleTimeString('en-US', {hour:'numeric',minute:'2-digit',hour12:true})}
                </div>
                <div style={{fontSize:11,color:'#8A9E8A'}}>Wednesday, April 2, 2025</div>
              </div>
              <div>
                <label style={labelStyle}>Department</label>
                <select value={department} onChange={e=>setDepartment(e.target.value)} style={inputStyle}>
                  {['Operations','Administrative','Field Staff','Customer Service'].map(d=><option key={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Job / Program Code</label>
                <input value={jobCode} onChange={e=>setJobCode(e.target.value)} placeholder="e.g. OPS-001, HR-2025..." style={inputStyle}/>
              </div>
              <div>
                <label style={labelStyle}>Notes (optional)</label>
                <textarea value={clockNote} onChange={e=>setClockNote(e.target.value)} rows={2} placeholder="What are you working on today?" style={{...inputStyle,resize:'none'}}/>
              </div>
              <button onClick={handleClockIn} style={{padding:'12px',background:'#4A7C59',border:'none',borderRadius:6,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
                &#128336; Clock In Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{marginBottom:20,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <h1 style={{fontFamily:'Georgia,serif',fontSize:'1.4rem',color:'#2B2B2B',fontWeight:600}}>Good morning, Jamie.</h1>
          <p style={{fontSize:11,color:'#6B7D6B',marginTop:2}}>Wednesday, April 2, 2025 &middot; Week 14</p>
        </div>
        {clockedIn ? (
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:10,color:'#4A7C59',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.08em'}}>Clocked In</div>
              <div style={{fontFamily:'Georgia,serif',fontSize:18,color:'#4A7C59',fontWeight:700,letterSpacing:'0.05em'}}>{formatElapsed(elapsed)}</div>
              <div style={{fontSize:10,color:'#8A9E8A'}}>since {clockInTime}</div>
            </div>
            <button onClick={handleClockOut} style={{padding:'10px 18px',background:'#A0392B',border:'none',borderRadius:6,color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
              Clock Out
            </button>
          </div>
        ) : (
          <button onClick={()=>setShowClockModal(true)} style={{padding:'10px 18px',background:'#4A7C59',border:'none',borderRadius:6,color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:"'Montserrat',sans-serif"}}>
            + Clock In
          </button>
        )}
      </div>

      {/* Clock status bar - shows when clocked in */}
      {clockedIn && (
        <div style={{marginBottom:16,padding:'12px 16px',background:'rgba(74,124,89,0.06)',border:'1px solid rgba(74,124,89,0.2)',borderRadius:8,display:'flex',alignItems:'center',gap:12}}>
          <div style={{width:8,height:8,borderRadius:'50%',background:'#4A7C59',flexShrink:0,animation:'pulse 2s infinite'}}></div>
          <div style={{flex:1}}>
            <span style={{fontSize:12,fontWeight:600,color:'#4A7C59'}}>Currently clocked in</span>
            <span style={{fontSize:11,color:'#6B7D6B',marginLeft:8}}>
              {department} &middot; {jobCode || 'OPS-001'}
              {clockNote && ` &middot; "${clockNote}"`}
            </span>
          </div>
          <div style={{fontSize:12,color:'#4A7C59',fontWeight:600}}>Today: {(todayTotal).toFixed(2)}h</div>
        </div>
      )}

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:20}}>
        {[
          {label:'Hours This Week', value: clockedIn ? (todayTotal + elapsed/3600).toFixed(1) : todayTotal.toFixed(1), pill:'On Track', pillColor:'#4A7C59'},
          {label:'PTO Balance', value:'11', pill:'2 pending', pillColor:accent},
          {label:'Tasks Due', value:'3', pill:'1 urgent', pillColor:'#4A6B8A'},
          {label:'Days Until Review', value:'47', pill:'June 2025', pillColor:'#6B7D6B'},
        ].map(s => (
          <div key={s.label} style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,padding:'14px 16px'}}>
            <div style={{fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:500,marginBottom:4}}>{s.label}</div>
            <div style={{fontFamily:'Georgia,serif',fontSize:'1.5rem',color:'#2B2B2B',fontWeight:600,lineHeight:1}}>{s.value}</div>
            <div style={{marginTop:4,display:'inline-block',padding:'1px 6px',borderRadius:8,fontSize:10,fontWeight:600,background:s.pillColor+'22',color:s.pillColor}}>{s.pill}</div>
          </div>
        ))}
      </div>

      {/* Main grid */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:14}}>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>

          {/* Today's Punches */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Today's Time Log</div>
              <span style={{fontSize:11,color:'#6B7D6B'}}>Total: <strong style={{color:'#2B2B2B'}}>{todayTotal.toFixed(2)}h</strong></span>
            </div>
            {punches.length === 0 ? (
              <div style={{padding:'24px',textAlign:'center',color:'#8A9E8A',fontSize:12}}>No punches yet today. Clock in to start tracking.</div>
            ) : (
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:'#F7F2EB'}}>
                    {['In','Out','Department','Code','Notes','Hours'].map(h=>(
                      <th key={h} style={{textAlign:'left',padding:'7px 14px',fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em',color:'#6B7D6B',fontWeight:600,borderBottom:'1px solid #EDE6DA'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {punches.map((p,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #EDE6DA'}}>
                      <td style={{padding:'10px 14px',color:'#4A4A4A',fontWeight:500}}>{p.in}</td>
                      <td style={{padding:'10px 14px'}}>
                        {p.out
                          ? <span style={{color:'#4A4A4A',fontWeight:500}}>{p.out}</span>
                          : <span style={{color:'#4A7C59',fontWeight:600,fontSize:10}}>Active</span>
                        }
                      </td>
                      <td style={{padding:'10px 14px',color:'#6B7D6B'}}>{p.dept}</td>
                      <td style={{padding:'10px 14px'}}>
                        <span style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:'rgba(74,107,138,0.08)',color:'#4A6B8A',fontWeight:600}}>{p.code}</span>
                      </td>
                      <td style={{padding:'10px 14px',color:'#8A9E8A',fontSize:11}}>{p.note || '--'}</td>
                      <td style={{padding:'10px 14px',fontWeight:600,color:p.hours?'#2B2B2B':'#4A7C59'}}>
                        {p.hours ? p.hours.toFixed(2)+'h' : formatElapsed(elapsed)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* To-Dos */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>My To-Do's</div>
              <span style={{fontSize:11,color:accent,cursor:'pointer'}} onClick={()=>navigate('/memos')}>View all</span>
            </div>
            {todos.map((t,i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 16px',borderBottom:'1px solid #EDE6DA'}}>
                <div style={{width:18,height:18,borderRadius:4,border:t.done?'none':'1.5px solid #D9C3A3',background:t.done?'#4A7C59':'#fff',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  {t.done && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2 2 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/></svg>}
                </div>
                <span style={{flex:1,fontSize:12,color:t.done?'#8A9E8A':'#4A4A4A',textDecoration:t.done?'line-through':'none'}}>{t.text}</span>
                <span style={{fontSize:10,padding:'2px 7px',borderRadius:8,fontWeight:600,background:t.badgeColor+'22',color:t.badgeColor}}>{t.badge}</span>
              </div>
            ))}
          </div>

          {/* Memos */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Recent Memos</div>
              <span style={{fontSize:11,color:accent,cursor:'pointer'}}>View all</span>
            </div>
            {memos.map((m,i) => (
              <div key={i} style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',cursor:'pointer',borderLeft:m.unread?`3px solid ${accent}`:'3px solid transparent'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                  <div style={{fontSize:12,fontWeight:m.unread?600:500,color:m.unread?accent:'#2B2B2B'}}>{m.title}</div>
                  <div style={{fontSize:10,color:'#8A9E8A',flexShrink:0,marginLeft:8}}>{m.date}</div>
                </div>
                <div style={{fontSize:11,color:'#6B7D6B'}}>{m.preview}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right column */}
        <div style={{display:'flex',flexDirection:'column',gap:14}}>

          {/* Calendar */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>April 2025</div>
              <span style={{fontSize:11,color:accent,cursor:'pointer'}} onClick={()=>navigate('/calendar')}>Full calendar →</span>
            </div>
            <div style={{padding:'12px'}}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',marginBottom:4}}>
                {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                  <div key={d} style={{textAlign:'center',fontSize:9,color:'#6B7D6B',fontWeight:600,letterSpacing:'0.06em',textTransform:'uppercase',padding:'2px 0'}}>{d}</div>
                ))}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2}}>
                {calDays.map((d,i) => (
                  <div key={i} style={{textAlign:'center',fontSize:11,padding:'4px 2px',borderRadius:4,cursor:d.label?'pointer':'default',...calColor(d.type)}}>{d.label}</div>
                ))}
              </div>
              <div style={{display:'flex',gap:12,marginTop:10,paddingTop:10,borderTop:'1px solid #EDE6DA',flexWrap:'wrap'}}>
                {[[accent,'Today'],['rgba(107,125,107,0.4)','Event'],['rgba(217,195,163,0.6)','My PTO']].map(([bg,label]) => (
                  <div key={label} style={{display:'flex',alignItems:'center',gap:4,fontSize:10,color:'#6B7D6B'}}>
                    <div style={{width:8,height:8,borderRadius:2,background:bg}}></div>{label}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{background:'#fff',border:'1px solid #EDE6DA',borderRadius:10,overflow:'hidden'}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #EDE6DA'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#2B2B2B'}}>Quick Actions</div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,padding:12}}>
              {[
                {label:'Submit Timesheet', sub:'Week 13 pending',    color:accent,    Icon:FileText,  path:'/timesheets'},
                {label:'Request Time Off', sub:'11 days available', color:'#6B7D6B', Icon:Umbrella,  path:'/time-off'},
                {label:'Helpdesk Request', sub:'IT & Facilities',   color:'#D9C3A3', Icon:HelpCircle,path:'/requests'},
                {label:'View Documents',  sub:'Policies & forms',  color:'#6B7D6B', Icon:FolderOpen,path:'/documents'},
              ].map(a => (
                <button key={a.label} onClick={()=>navigate(a.path||'/')} style={{padding:'10px',border:'1px solid #EDE6DA',borderRadius:8,background:'#F7F2EB',cursor:'pointer',textAlign:'left',transition:'all 0.15s',fontFamily:"'Montserrat',sans-serif"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=a.color;e.currentTarget.style.background='#fff'}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='#EDE6DA';e.currentTarget.style.background='#F7F2EB'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                    {a.Icon && <div style={{width:28,height:28,borderRadius:6,background:a.color+'18',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><a.Icon size={14} color={a.color} strokeWidth={1.75}/></div>}
                    <div style={{fontSize:11,fontWeight:600,color:'#2B2B2B'}}>{a.label}</div>
                  </div>
                  <div style={{fontSize:10,color:'#8A9E8A',paddingLeft:a.Icon?36:0}}>{a.sub}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
